import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";

import { atlasRoutes } from "@/app/router/routes";
import { LookupByUuidForm } from "@/components/forms/lookup-by-uuid-form";
import { AccountsSearchForm } from "@/pages/accounts/root/accounts-search-form";
import { AccountsTable } from "@/pages/accounts/root/accounts-table";
import { AccountSummarySchema } from "@/domains/openapi/types";
import { api } from "@/modules/api";

export const AccountsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);

  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    setSearchTerm(query);
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["accounts-search", searchTerm],
    queryFn: () =>
      api.post("/internal/v1/accounts/search", {
        body: {
          query_string: searchTerm,
          limit: pageSize,
        },
      }),
    enabled: searchTerm.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleSearch = (query: string) => {
    if (query) {
      searchParams.set("query", query);
    } else {
      searchParams.delete("query");
    }
    setSearchParams(searchParams);
  };

  const handleViewAccount = (id: string) => {
    navigate(atlasRoutes.accountsView({ params: { accountId: id } }).path);
  };

  const tableData = useMemo<AccountSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  const errorMessage =
    error && error instanceof Error ? error.message : isError ? "Error fetching accounts." : null;

  return (
    <Container>
      <h1>Accounts</h1>
      <DescriptionText>Search by name, email address, or account ID.</DescriptionText>
      <br />

      <Section>
        <SectionTitle>Lookup By UUID</SectionTitle>
        <LookupByUuidForm
          onSubmit={uuid =>
            navigate(atlasRoutes.accountsView({ params: { accountId: uuid } }).path)
          }
        />
      </Section>

      <Section>
        <SectionTitle>Search Accounts</SectionTitle>
        <AccountsSearchForm initialValue={searchTerm} onSearch={handleSearch} />

        {isLoading && <div>Loading...</div>}
        {errorMessage && <div>{errorMessage}</div>}

        {tableData.length > 0 ? (
          <AccountsTable data={tableData} onViewAccount={handleViewAccount} pageSize={pageSize} />
        ) : (
          !isLoading &&
          searchTerm && (
            <div>
              <Text size="2">No accounts found.</Text>
            </div>
          )
        )}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const Section = styled.section`
  margin-top: 32px;
`;

const SectionTitle = styled(Text)`
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
`;
